import React, { useMemo } from 'react'
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
} from '@mui/material'
import type {
	LoanComparisonScenario,
	RunLOCalculation,
} from '@matech/thebigpos-sdk'
import { applyMask } from '../../../../services/utils'
import { Loading } from '../../../../components/Loading'
import { LoadingBtn } from '../../../../components/Button'

const loanPurposeMap = {
	Purchase: 'Purchase',
	'NoCash-Out Refinance': 'Refi',
	'Cash-Out Refinance': 'Cash Out Refi',
}

const escrowMap = {
	Y: 'Taxes and Insurance',
	N: 'No Escrow',
}

const propertyOccupancyMap = {
	PrimaryResidence: 'Primary Residence',
	SecondHome: 'Second Home',
	InvestmentProperty: 'Investment',
}

const propertyTypeMap = {
	SingleFamily: 'Single Family',
	MultiFamily: 'Multi Family 2-4 Units',
	Condo: 'Condo',
	TownHome: 'Town Home',
	CoOp: 'Co-Op',
}

export type LoanComparisonScenarioProps = {
	index: number
	scenario: LoanComparisonScenario
	loanData: RunLOCalculation
	loading?: boolean
	onDelete: (index: number) => void
}

const LoanComparisonScenarioTable = ({
	index,
	scenario,
	loanData,
	loading = false,
	onDelete,
}: LoanComparisonScenarioProps) => {
	const {
		loanPurpose,
		zipCode,
		creditScore,
		taxes,
		insurance,
		escrow,
		loanTerm1,
		loanTerm2,
		propertyOccupancy,
		propertyType,
		county,
		flood,
		hoa,
		borrowerIncome,
	} = loanData

	const {
		loanType,
		propertyValue,
		downpaymentAmount,
		downpaymentPercent,
		loanAmount,
		totalAssets,
		monthlyPayment,
		totalMortgageAmount,
		dtiFront,
		dtiBack,
		ltvFront,
		ltvBack,
		totalCashToClose,
		apr,
		mi,
		miFactor,
		rate,
	} = scenario

	const handleDelete = () => {
		onDelete(index)
	}

	const hasValues = useMemo(() => {
		const hasMonthlyPayment = !!monthlyPayment
		const hasDtiFront = !!dtiFront
		const hasDtiBack = !!dtiBack
		const hasLtvFront = !!ltvFront
		const hasLtvBack = !!ltvBack
		const hasTotalCashToClose = !!totalCashToClose
		const hasApr = !!apr

		return (
			hasMonthlyPayment ||
			hasDtiFront ||
			hasDtiBack ||
			hasLtvFront ||
			hasLtvBack ||
			hasTotalCashToClose ||
			hasApr
		)
	}, [
		apr,
		dtiBack,
		dtiFront,
		ltvBack,
		ltvFront,
		monthlyPayment,
		totalCashToClose,
	])

	return (
		<div>
			<p className="text-lg font-rubik font-bold dark:text-white">
				Scenario {index + 2}
			</p>
			<div className="my-3">
				<LoadingBtn
					loading={loading}
					text="Clear"
					onClick={handleDelete}
				/>
			</div>
			{loading && <Loading size="small" />}
			{!loading && hasValues && (
				<>
					<TableContainer className="w-100">
						<Table aria-label="loan scenario table">
							<TableBody>
								<TableRow>
									<TableCell align="left">Loan Purpose</TableCell>
									<TableCell align="right">
										{loanPurpose
											? loanPurposeMap[
													loanPurpose as keyof typeof loanPurposeMap
												]
											: ''}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Loan Type</TableCell>
									<TableCell align="right">{loanType}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Property Value</TableCell>
									<TableCell align="right">
										${applyMask(propertyValue, 'fullCurrency')}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">
										Down Payment Amount
									</TableCell>
									<TableCell align="right">
										${applyMask(downpaymentAmount, 'fullCurrency')}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">
										Down Payment Percent
									</TableCell>
									<TableCell align="right">
										{downpaymentPercent}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Loan Amount</TableCell>
									<TableCell align="right">
										${applyMask(loanAmount, 'fullCurrency')}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Zip Code</TableCell>
									<TableCell align="right">{zipCode}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Credit Score</TableCell>
									<TableCell align="right">{creditScore}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Monthly Taxes</TableCell>
									<TableCell align="right">
										${applyMask(taxes, 'fullCurrency')}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">
										Monthly Insurance
									</TableCell>
									<TableCell align="right">
										${applyMask(insurance, 'fullCurrency')}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Escrow</TableCell>
									<TableCell align="right">
										{escrow
											? escrowMap[escrow as keyof typeof escrowMap]
											: ''}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Total Assets</TableCell>
									<TableCell align="right">
										${applyMask(totalAssets, 'fullCurrency')}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Loan Term 1</TableCell>
									<TableCell align="right">{loanTerm1}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Loan Term 2</TableCell>
									<TableCell align="right">{loanTerm2}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">
										Property Occupancy
									</TableCell>
									<TableCell align="right">
										{propertyOccupancy
											? propertyOccupancyMap[
													propertyOccupancy as keyof typeof propertyOccupancyMap
												]
											: ''}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Home Type</TableCell>
									<TableCell align="right">
										{propertyType
											? propertyTypeMap[
													propertyType as keyof typeof propertyTypeMap
												]
											: ''}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Borrower Income</TableCell>
									<TableCell align="right">
										${applyMask(borrowerIncome, 'fullCurrency')}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Rate</TableCell>
									<TableCell align="right">{rate}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">County</TableCell>
									<TableCell align="right">{county}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Monthly Flood</TableCell>
									<TableCell align="right">{flood}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">Monthly HOA</TableCell>
									<TableCell align="right">{hoa}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">MI Factor</TableCell>
									<TableCell align="right">{miFactor}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell align="left">MI</TableCell>
									<TableCell align="right">{mi}</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
					<div className="mt-4">
						<p className="text-xl font-rubik font-bold md:mr-2 dark:text-white">
							Calculated Values
						</p>
						<TableContainer>
							<Table aria-label="calculated values table">
								<TableBody>
									<TableRow>
										<TableCell align="left">
											Monthly Payment
										</TableCell>
										<TableCell align="right">
											${applyMask(monthlyPayment, 'fullCurrency')}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell align="left">DTI Front</TableCell>
										<TableCell align="right">{dtiFront}</TableCell>
									</TableRow>
									<TableRow>
										<TableCell align="left">DTI Back</TableCell>
										<TableCell align="right">{dtiBack}</TableCell>
									</TableRow>
									<TableRow>
										<TableCell align="left">LTV</TableCell>
										<TableCell align="right">{ltvFront}</TableCell>
									</TableRow>
									<TableRow>
										<TableCell align="left">CLTV</TableCell>
										<TableCell align="right">{ltvBack}</TableCell>
									</TableRow>
									<TableRow>
										<TableCell align="left">Cash to Close</TableCell>
										<TableCell align="right">
											${applyMask(totalCashToClose, 'fullCurrency')}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell align="left">APR</TableCell>
										<TableCell align="right">{apr}</TableCell>
									</TableRow>
									<TableRow>
										<TableCell align="left">
											Total Mortgage Amount
										</TableCell>
										<TableCell align="right">
											$
											{applyMask(totalMortgageAmount, 'fullCurrency')}
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</TableContainer>
					</div>
				</>
			)}
			{!loading && !hasValues && (
				<p className="mt-2 text-center">No saved scenario</p>
			)}
		</div>
	)
}

export default LoanComparisonScenarioTable
